import { useSyncExternalStore } from "react";

export function useQueryParams() {
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { get, getAll, setParam } as const;
}

const subscribers = new Set<() => void>();

const notifyAll = () => subscribers.forEach((cb) => cb());

/**
 * Notify all `useQueryParams` consumers that the URL changed. `setParam` calls
 * this after its own history updates; the router also calls it after every
 * navigation so that same-path search changes made via `<Link>` (which bypass
 * `popstate`) still refresh components that read query params here.
 */
export function notifyQueryParamChange() {
  notifyAll();
}

function subscribe(callback: () => void) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

function getSnapshot() {
  return location.search;
}

function getServerSnapshot() {
  return "";
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", notifyAll);
}

function get(key: string): string | null {
  if (typeof location === "undefined") return null;
  return new URLSearchParams(location.search).get(key);
}

function getAll(key: string): string[] | null {
  if (typeof location === "undefined") return null;
  return new URLSearchParams(location.search).getAll(key);
}

function setParam(
  key: string,
  value: string | string[] | null,
  options: { replace?: boolean } = {},
): void {
  if (typeof location === "undefined") return;

  const params = new URLSearchParams(location.search);

  // Normalize arrays to comma-separated string
  const normalizedValue = Array.isArray(value)
    ? value.filter(Boolean).join(",") || null
    : value;

  if (normalizedValue === null || normalizedValue === "") {
    params.delete(key);
  } else {
    params.set(key, normalizedValue);
  }

  const search = params.toString();
  const url = search ? `${location.pathname}?${search}` : location.pathname;

  if (options.replace) {
    history.replaceState(null, "", url);
  } else {
    history.pushState({}, "", url);
  }
  notifyAll();
}
